import { X } from 'lucide-react';
import { useIsDesktop } from '../../hooks/useMediaQuery';
import OnboardingCallBooking from '../../pages/OnboardingCallBooking';
import { Modal } from './ui/Modal';

interface SchedulerModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the flow closes itself (X, cancel, or after booking). */
  onClose: () => void;
  /** Fired once a booking is confirmed. */
  onBooked?: () => void;
  source?: string;
}

/**
 * Onboarding-call scheduler shown over the current context.
 *
 * Mobile: a full-screen panel (what Calendly/Cal.com do on phones). Desktop: a centered panel.
 * Both are a flex column with a fixed close row and ONE internal scroll region (overscroll-contain
 * so a drag never chains to the page). Rendered via the sticky-safe <Modal> primitive, whose
 * scroll lock never mutates <body> — so the sticky header stays put even when opened while scrolled.
 *
 * The deep-link route (/portal/onboarding-call) still renders the same flow as a full page.
 */
export function SchedulerModal({ open, onOpenChange, onClose, onBooked, source }: SchedulerModalProps) {
  const isDesktop = useIsDesktop();

  // Only mount the flow while open so its data loads fresh each time (and unmounts on close).
  const body = open ? <OnboardingCallBooking onClose={onClose} onBooked={onBooked} source={source} /> : null;

  // Shared timing so overlay + panel move together: open graceful (--dur-base / ease-out),
  // close quick (--dur-fast / ease-in). One source of truth via the motion tokens.
  const dialogAnim =
    'data-[state=open]:duration-[var(--dur-base)] data-[state=closed]:duration-[var(--dur-fast)] ' +
    'data-[state=open]:ease-[var(--ease-out)] data-[state=closed]:ease-[var(--ease-in)]';

  const contentClass = isDesktop
    ? `fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col overflow-hidden rounded-lg border border-border bg-background shadow-lg data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 ${dialogAnim}`
    : `fixed inset-0 z-50 flex flex-col bg-background data-[state=open]:animate-in data-[state=open]:slide-in-from-bottom-4 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-4 ${dialogAnim}`;

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      aria-label="Schedule your onboarding call"
      overlayClassName={`fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 ${dialogAnim}`}
      className={contentClass}
    >
      {/* Close row — stays put while the body scrolls. Neutral focus ring (no gold). Left on the
          mobile full-screen takeover (native pattern), top-right on the desktop dialog. */}
      <div className="flex shrink-0 items-center justify-start px-2 py-2 md:justify-end">
        <button
          type="button"
          aria-label="Close"
          onClick={() => onOpenChange(false)}
          className="grid h-9 w-9 place-items-center rounded-md text-muted-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-border md:hover:text-foreground"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-0 md:px-6 md:pb-6">
        {body}
      </div>
    </Modal>
  );
}

export default SchedulerModal;
